import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridColDef,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import useAuth from 'auth/UseAuth';
import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { GetSampleTrackingSelectedSamples, SampleTrackingSelectedTableData } from '../../data/SampleTrackingData';
import { getSampleIdentifier, Sample } from '../../data/SampleData';
import { CreateSampleTrackingTransitionModal } from './CreateSampleTrackingTransitionModal';
import { AuthorizedSection } from '../../auth/AuthorizedSection';
import { FlexTableBox } from '../../components/FlexTableBox';
import {
  sampleTag,
  copyBtn,
  foreignHash,
  sampleBbid,
  sampleId,
  sampleIdentifier,
  stGridFulfillmentPlanSampleSelectionStatus,
  stGridSampleAvailability,
  stGridSampleBioBankId,
  stGridSampleCollectionDate,
  stGridSampleLabId,
  stGridTransitionSampleTransitionId,
} from '../../util/Constants';
import { map, uniq } from 'lodash';
import { SampleTrackingRowSelectionModal } from './SampleTrackingRowSelectionModal';
import { LoadingState, LoadState } from '../../components/LoadingStateUtil';
import useMemoTranslation from '../../hooks/UseMemoTranslation';
import { renderSampleHierarchyGridCell } from '../../components/grid/cell/SampleHierarchyGridCell';
import { DecryptForeignHashesSwitch } from '../../components/DecryptForeignHashesSwitch';
import { SampleTrackingGridWrapper } from '../../components/grid/SampleTrackingGridWrapper';
import { sampleTrackingGridDefaultColumnOverrides } from '../../util/grid/TableUtils';
import { renderCopyTextGridCell } from '../../components/grid/cell/CopyTextGridCell';
import { GroupByAttribute, SampleTrackingGroupByAttributes } from '../../components/SampleTrackingGroupByAttributes';
import { sampleTagGridColumnDef, SampleTrackingTagSamplesModal } from './SampleTrackingTagSamplesModal';
import { SampleTrackingDuplicateSamplesModal } from './SampleTrackingDuplicateSamplesModal';
import { SampleTrackingConfigProvider } from '../../contexts/SampleTrackingConfigContext';

type Row = {
  id: number;
  sampleIdentifier: string;
  currentStage?: string;
  originLab?: string;
  originBiobank?: string;
  groupByPath?: string[] | undefined;
} & Sample;

export interface SampleTrackingSelectedSampleGridProps {
  researchProjectId: string;
  sampleGroup?: string;
}

type SelectedSampleDataGroupByAttribute = GroupByAttribute<SampleTrackingSelectedTableData, Row>;

/** Unique identifier for this component. Used for view management persistence. Should not be changed once set. */
export const COMPONENT_NAME = 'SampleTrackingSelectedSampleGrid';

export const selectedScopeOptions = [
  { key: 'researchProject', label: 'This project', visible: true },
  { key: 'none', label: 'All projects', visible: true },
];

export const SampleTrackingSelectedSampleGrid = ({
  researchProjectId,
  sampleGroup,
}: SampleTrackingSelectedSampleGridProps) => {
  const { accessToken } = useAuth();
  const apiRef = useGridApiRef();

  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'Loading' });
  const [refreshTrigger, setRefreshTrigger] = useState<boolean>(true);

  const [rawData, setRawData] = useState<ReadonlyArray<SampleTrackingSelectedTableData>>([]);
  const [selectedSampleIds, setSelectedSampleIds] = useState<string[]>([]);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>();
  const [useDecryptedHashes, setUseDecryptedHashes] = useState<boolean>(false);
  const [groupByEnabled, setGroupByEnabled] = useState<boolean>(false);
  const [selectedGroupByAttributeId, setSelectedGroupByAttributeId] = useState<string>('groupByPatientDeidentity');
  const [selectedGroupByAttribute, setSelectedGroupByAttribute] = useState<SelectedSampleDataGroupByAttribute>();

  const columns = useColumns();
  const rows = useRows(rawData, selectedGroupByAttribute);

  const { t } = useMemoTranslation();

  useEffect(() => {
    return LoadState(setLoadingState, async () => {
      if (!accessToken || !researchProjectId) {
        return;
      }

      const data = await GetSampleTrackingSelectedSamples(
        researchProjectId,
        accessToken,
        useDecryptedHashes,
        sampleGroup
      );

      setRawData(data);
    });
  }, [accessToken, researchProjectId, useDecryptedHashes, sampleGroup, refreshTrigger]);

  useEffect(() => {
    if (!selectionModel) {
      return;
    }

    const sampleIds: string[] = [];
    selectionModel.forEach(selected => {
      const id = apiRef.current?.getRow(selected)?.sampleId;
      if (id) {
        sampleIds.push(id);
      }
    });

    setSelectedSampleIds(uniq(sampleIds));
  }, [selectionModel, apiRef]);

  const groupByAttributes = useMemo<SelectedSampleDataGroupByAttribute[]>(
    () => [
      {
        id: 'groupByPatientDeidentity',
        displayValue: t('groupByPatientDeidentity'),
        groupByPath: (resultRow: Row, ...rowData: SampleTrackingSelectedTableData[]) => {
          const row = rowData[0];
          resultRow.groupByPath = [row.sample.patientDeidentity || '', row.sample.sampleId];
          return resultRow;
        },
        groupingColumnDef: () => {
          return {
            headerName: t('byPatientDeidentity'),
            minWidth: 300,
            maxWidth: 900,
            //@ts-ignore  // allow implicit any
            valueFormatter: params => {
              const node = params.api.getRowNode(params.id!)!;
              if (node.type !== 'group') {
                return '';
              }

              return node.groupingKey;
            },
          };
        },
        show: true,
      },
    ],
    [t]
  );

  useEffect(() => {
    if (groupByAttributes.length === 0) {
      return;
    }

    if (groupByEnabled) {
      setSelectedGroupByAttribute(groupByAttributes.find(attr => attr.id === selectedGroupByAttributeId));
    } else {
      setSelectedGroupByAttribute(undefined);
    }
  }, [groupByAttributes, groupByEnabled, selectedGroupByAttributeId]);

  function refreshData(hasChanges: boolean, selectionModel: GridRowSelectionModel = []) {
    if (!hasChanges) {
      return;
    }

    setRefreshTrigger(currentState => !currentState);
    setSelectionModel(selectionModel);
  }

  return (
    <>
      <FlexTableBox>
        <SampleTrackingConfigProvider researchProjectId={researchProjectId}>
          <SampleTrackingGridWrapper
            apiRef={apiRef}
            leftActions={
              <Box display='flex' flexDirection='row' alignItems='center'>
                <DecryptForeignHashesSwitch decryptedHashesProps={[useDecryptedHashes, setUseDecryptedHashes]} />
                <SampleTrackingGroupByAttributes<SampleTrackingSelectedTableData, Row>
                  enabled={groupByEnabled}
                  setEnabled={setGroupByEnabled}
                  items={groupByAttributes}
                  selectedId={selectedGroupByAttributeId}
                  setSelectedId={setSelectedGroupByAttributeId}
                />
              </Box>
            }
            rightActions={
              <Box display='flex' flexDirection='row-reverse' alignItems='center'>
                <AuthorizedSection hasSampleTrackingWriteAccess>
                  <CreateSampleTrackingTransitionModal
                    researchProjectId={researchProjectId}
                    enableCheckForAlreadyProcessedSamples={true}
                    selectedSamples={map(selectedSampleIds, i => {
                      const item = rows.find(j => j.sampleId === i);

                      return {
                        ...item,
                        sampleIdentifier: item?.sampleIdentifier ?? '',
                        patientDeidentity: item?.patientDeidentity,
                      } as any;
                    })}
                    onClose={refreshData}
                  />
                  <SampleTrackingDuplicateSamplesModal
                    samplesToCheck={map(selectedSampleIds, i => {
                      const item = rows.find(j => j.sampleId === i);

                      return { ...item, patientDeidentity: item?.patientDeidentity ?? [] } as any;
                    })}
                    researchProjectId={researchProjectId}
                    autoConfirmWhenNoResultsFound={true}
                    onConfirm={(_, unselectedSampleIds, reloadData, ___) => {
                      // de-select the un-selected samples, leaving only the explicitly selected ones and any that were not duplicates
                      const unselectedSet = new Set(unselectedSampleIds);

                      const newSelectionModel = (selectionModel ?? [])
                        .map(selected => {
                          const id = apiRef.current?.getRow(selected)?.sampleId;
                          if (!id) {
                            return -1; // remove the sample from the selection
                          }

                          // if the sample is in the unselected set
                          if (unselectedSet.has(id)) {
                            return -1; // remove the sample from the selection
                          }

                          // Everything else stays selected
                          return selected;
                        })
                        .filter(selected => (selected as number) > -1);

                      // only need to reload data if there are unselected samples or if explicitly requested
                      refreshData(unselectedSet.size > 0 || reloadData, newSelectionModel as GridRowSelectionModel);
                    }}
                  />
                  {/* Removing this but not permanently. See: https://ovation-io.atlassian.net/browse/DATA-1993*/}
                  {/* <SampleTrackingExhaustModal selectedSampleIds={selectedSampleIds} onClose={refreshData} /> */}
                  <SampleTrackingTagSamplesModal
                    researchProjectId={researchProjectId}
                    selectedSamples={map(selectedSampleIds, i => {
                      const item = rows.find(j => j.sampleId === i);

                      return { ...item, sampleTags: item?.sampleTags ?? [] } as any;
                    })}
                    onClose={(hasChanges: boolean) => {
                      refreshData(hasChanges);
                    }}
                  />
                  <SampleTrackingRowSelectionModal
                    gridApiRef={apiRef}
                    sampleIdProperties={[foreignHash, sampleIdentifier, sampleBbid, sampleId]}
                    setSelectionModel={setSelectionModel}
                  />
                </AuthorizedSection>
              </Box>
            }
            loadingState={loadingState}
            rows={rows}
            columns={columns}
            initialState={{
              pinnedColumns: {
                left: [GRID_CHECKBOX_SELECTION_FIELD, GRID_TREE_DATA_GROUPING_FIELD, sampleTag, copyBtn],
              },
              sorting: {
                sortModel: [{ field: sampleIdentifier, sort: 'asc' }],
              },
            }}
            checkboxSelection
            getTreeDataPath={row => row.groupByPath || []}
            treeData={groupByEnabled}
            groupingColDef={() => {
              return (
                selectedGroupByAttribute?.groupingColumnDef() || {
                  headerName: t('unknown'),
                  minWidth: 300,
                }
              );
            }}
            defaultGroupingExpansionDepth={1}
            disableRowSelectionOnClick
            onRowSelectionModelChange={newSelectionModel => setSelectionModel(newSelectionModel)}
            rowSelectionModel={selectionModel}
            loadingProps={[loadingState, setLoadingState]}
            defaultColumnOrdering={[
              sampleTag,
              copyBtn,
              sampleIdentifier,
              stGridTransitionSampleTransitionId,
              stGridSampleLabId,
              stGridSampleBioBankId,
              stGridFulfillmentPlanSampleSelectionStatus,
              stGridSampleCollectionDate,
              stGridSampleAvailability,
            ]}
            cacheKey={'sample-tracking-selected-sample-grid'}
            columnDefinitionOverrides={sampleTrackingGridDefaultColumnOverrides}
            refreshTrigger={refreshTrigger}
            componentName={COMPONENT_NAME}
            researchProjectId={researchProjectId}
          />
        </SampleTrackingConfigProvider>
      </FlexTableBox>
    </>
  );
};

const useColumns = (): GridColDef[] => {
  const { t } = useMemoTranslation();

  return useMemo(
    () => [
      sampleTagGridColumnDef(sampleTag, t(sampleTag), t),
      {
        field: copyBtn,
        headerName: t(copyBtn),
        headerAlign: 'center',
        align: 'center',
        maxWidth: 30,
        renderCell: renderCopyTextGridCell,
        valueGetter: params => params.row.sampleIdentifier,
      },
      {
        field: sampleIdentifier,
        headerName: t(sampleIdentifier),
        headerAlign: 'left',
        align: 'left',
        width: 350,
        maxWidth: 600,
        renderCell: params =>
          renderSampleHierarchyGridCell({
            ...params,
            sampleId: params.row.sampleId,
            displayValue: params.value,
          }),
        valueGetter: params => params.value,
      },
    ],
    [t]
  );
};

const useRows = (
  samples: ReadonlyArray<SampleTrackingSelectedTableData>,
  selectedGroupByAttribute?: SelectedSampleDataGroupByAttribute | undefined
): ReadonlyArray<Row> => {
  return useMemo(() => {
    return samples.map((r, index) => {
      const resultRow = {
        id: index++,
        ...r.sample,
        sampleIdentifier: getSampleIdentifier(r.sample, r.labAssignedSampleLabel),
      };

      if (selectedGroupByAttribute) {
        selectedGroupByAttribute.groupByPath(resultRow as Row, r);
      }

      return resultRow;
    });
  }, [samples, selectedGroupByAttribute]);
};
